// DropZone, useFileDrop and DropVeil: where a file someone is holding lands. The zone is a pad
// with a heavy dashed edge and an invisible input over all of it; it lights while a file is
// inbound and closes its edge into a ring of light once one has landed. useFileDrop is the same
// drop handling for a whole region (a page, a panel), with DropVeil drawn over it while a file
// is inbound. docs/operations/design-standard.md, "Controls".

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton } from './Button';
import FadeIn from './FadeIn';
import { IconCheck, IconClose } from './icons';
import LogoSpinner from './LogoSpinner';
import { MS, usePresence } from './motion';
import Tooltip from './Tooltip';

export type RejectReason = 'type' | 'count' | 'busy';

/** Whether `file` fits an `accept` list as the picker reads it: `.ext`, `type/*` or an exact type. */
export function acceptsFile(file: File, accept?: string): boolean {
  const rules = (accept || '').split(',').map((r) => r.trim().toLowerCase()).filter(Boolean);
  if (!rules.length) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return rules.some((rule) => {
    if (rule.startsWith('.')) return name.endsWith(rule);
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

/** An accept list in words: ".pdf, .png or image files". Types too long to read are left out. */
export function describeAccept(accept?: string): string {
  const words = (accept || '').split(',').map((r) => r.trim().toLowerCase()).filter(Boolean).map((rule) => {
    if (rule.startsWith('.')) return rule;
    if (rule.endsWith('/*')) return `${rule.slice(0, -2)} files`;
    const sub = rule.split('/')[1] || '';
    return sub && !sub.includes('.') && !sub.includes('-') ? `.${sub}` : '';
  }).filter(Boolean);
  const unique = Array.from(new Set(words));
  if (unique.length < 2) return unique[0] || '';
  return `${unique.slice(0, -1).join(', ')} or ${unique[unique.length - 1]}`;
}

export const fileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

const isFileDrag = (e: { dataTransfer: DataTransfer | null }): boolean =>
  !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');

const isNativeFileInput = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement && target.type === 'file';

export interface FileDropOptions {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  /** A drop while busy still lands, and is handed to onReject as 'busy' rather than taken. */
  busy?: boolean;
  /** Refuses drops outright: the pointer shows "not here" and nothing lands. */
  disabled?: boolean;
  /** Files that did not fit: the wrong kind ('type'), past the first on a single zone ('count'), or dropped while busy. */
  onReject?: (files: File[], reason: RejectReason) => void;
}

export interface FileDrop {
  /** A file is being held over the target. Never true while disabled. */
  dragging: boolean;
  /** Spread onto the drop target. */
  bind: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  /** Sorts files from any source (a picker, a paste) the way a drop is sorted. */
  take: (list: FileList | readonly File[] | null) => void;
}

/**
 * Drop handling for any target. Only drags carrying files count, so dragging a row or some
 * text across the target does nothing. While mounted, a file drop that misses every target is
 * refused instead of opening the file in the tab.
 */
export function useFileDrop(options: FileDropOptions): FileDrop {
  const [dragging, setDragging] = useState(false);
  // dragenter and dragleave fire for every child crossed; the depth keeps the lit state steady.
  const depth = useRef(0);
  const latest = useRef(options);
  latest.current = options;

  const take = useCallback((list: FileList | readonly File[] | null) => {
    const { onFiles, onReject, accept, multiple } = latest.current;
    const all = list ? Array.from(list) : [];
    const fits = all.filter((f) => acceptsFile(f, accept));
    const wrong = all.filter((f) => !acceptsFile(f, accept));
    const taken = multiple ? fits : fits.slice(0, 1);
    const extra = multiple ? [] : fits.slice(1);
    // Taken first, rejections after: a zone clears its message on a take and sets it on a
    // rejection, so a partly good drop ends showing what was refused.
    if (taken.length) onFiles(taken);
    if (extra.length) onReject?.(extra, 'count');
    if (wrong.length) onReject?.(wrong, 'type');
  }, []);

  useEffect(() => {
    const reset = () => { depth.current = 0; setDragging(false); };
    // Native listeners on window run after React's root listeners, so defaultPrevented here
    // means a target on the page already took the drag.
    const over = (e: DragEvent) => {
      if (e.defaultPrevented || !isFileDrag(e) || isNativeFileInput(e.target)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    const drop = (e: DragEvent) => {
      reset();
      if (e.defaultPrevented || !isFileDrag(e) || isNativeFileInput(e.target)) return;
      e.preventDefault();
    };
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
      window.removeEventListener('dragend', reset);
    };
  }, []);

  const bind: FileDrop['bind'] = {
    onDragEnter: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      depth.current += 1;
      if (!latest.current.disabled) setDragging(true);
    },
    onDragOver: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = latest.current.disabled ? 'none' : 'copy';
    },
    onDragLeave: (e) => {
      if (!isFileDrag(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (!depth.current) setDragging(false);
    },
    onDrop: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      const { busy, disabled, onReject } = latest.current;
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files || []);
      if (busy) {
        if (files.length) onReject?.(files, 'busy');
        return;
      }
      take(files);
    },
  };

  return { dragging, bind, take };
}

/** A tray with an arrow leaving it. `lift` raises the arrow toward a file that is inbound. */
const IconUpload: React.FC<{ className?: string; lift?: boolean }> = ({ className = 'h-7 w-7', lift }) => (
  <svg aria-hidden="true" focusable="false" className={className} viewBox="0 0 24 24" fill="none">
    <g className={`transition-transform duration-spring ease-spring motion-reduce:translate-y-0 motion-reduce:transition-none ${lift ? '-translate-y-0.5' : ''}`}>
      <path d="M12 2.6a1.1 1.1 0 01.78.32l4.4 4.4a1.1 1.1 0 01-1.56 1.56L12 5.26 8.38 8.88a1.1 1.1 0 01-1.56-1.56l4.4-4.4A1.1 1.1 0 0112 2.6z" fill="currentColor" />
      <rect x="10.9" y="4.6" width="2.2" height="10.4" rx="1.1" fill="currentColor" />
    </g>
    <path d="M4 14.1a1.1 1.1 0 011.1 1.1v2.7c0 .5.4.9.9.9h12c.5 0 .9-.4.9-.9v-2.7a1.1 1.1 0 012.2 0v2.7a3.1 3.1 0 01-3.1 3.1H6A3.1 3.1 0 012.9 17.9v-2.7A1.1 1.1 0 014 14.1z" fill="currentColor" />
  </svg>
);

const rejectNote = (files: File[], reason: RejectReason, accept?: string): string => {
  if (reason === 'busy') return 'Still working on the last one. Drop this one again in a moment.';
  if (reason === 'count') return 'This takes one file at a time, so it kept the first.';
  const takes = describeAccept(accept);
  const what = files.length === 1 ? `${files[0].name} is not a file this takes.` : `${files.length} of those are not files this takes.`;
  return takes ? `${what} It takes ${takes}.` : what;
};

type ZoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'accept' | 'multiple' | 'disabled' | 'onChange' | 'className' | 'children' | 'size'
> & { 'data-testid'?: string };

let seq = 0;

export interface DropZoneProps {
  /** What goes here, as the words after "Drop" and "Choose": "the FAA CSV", "a datasheet PDF". */
  what: string;
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  /** What it takes, in words: "Only .csv files". Read out with the input. */
  hint?: React.ReactNode;
  /** What is on the pad now, held by the page. A list shows its count and total size. */
  chosen?: File | readonly File[] | null;
  /** Adds a remove key beside what is chosen. */
  onClear?: () => void;
  /** Words for the work under way ("Reading the CSV…"). While set, the zone takes nothing new. */
  busy?: string | false | null;
  disabled?: boolean;
  /** The page's own message about what landed. Takes the place of the zone's rejection note. */
  error?: string | null;
  /** Without it the zone says what it refused and why; with it, the page does. */
  onReject?: (files: File[], reason: RejectReason) => void;
  /** `sm` lays it in one row, for forms and dialogs. */
  size?: 'md' | 'sm';
  className?: string;
  inputProps?: ZoneInputProps;
}

export const DropZone = React.forwardRef<HTMLInputElement, DropZoneProps>(
  (
    { what, onFiles, accept, multiple = false, hint, chosen, onClear, busy, disabled = false, error, onReject, size = 'md', className = '', inputProps = {} },
    ref,
  ) => {
    const [uid] = useState(() => `ui-drop-${++seq}`);
    const [note, setNote] = useState<string | null>(null);
    const working = !!busy;

    const { dragging, bind, take } = useFileDrop({
      accept,
      multiple,
      busy: working,
      disabled,
      onFiles: (files) => { setNote(null); onFiles(files); },
      onReject: (files, reason) => {
        if (onReject) onReject(files, reason);
        else setNote(rejectNote(files, reason, accept));
      },
    });

    const files: readonly File[] = !chosen ? [] : Array.isArray(chosen) ? chosen : [chosen as File];
    const has = files.length > 0;
    const lit = dragging && !working && !disabled;
    const message = error || note;
    const live = !disabled && !working;
    const sm = size === 'sm';
    const landed = files.map((f) => `${f.name}:${f.size}:${f.lastModified}`).join('|');

    const ids = { chosen: `${uid}-chosen`, hint: `${uid}-hint`, message: `${uid}-msg` };
    const describedBy = [has && ids.chosen, hint && ids.hint, message && ids.message, inputProps['aria-describedby']]
      .filter(Boolean).join(' ') || undefined;

    useEffect(() => { if (dragging) setNote(null); }, [dragging]);
    // Not keyed on `landed` alone: a partly good drop lands a file and sets a note in one render,
    // and the note must survive it. Only the file going away, or work ending, retires a note.
    const hasLanded = landed !== '';
    useEffect(() => { setNote(null); }, [working, hasLanded]);
    const takeIdle = useCallback((list: FileList | readonly File[] | null) => { if (!working) take(list); }, [working, take]);

    let surface: string;
    if (disabled) surface = 'border-outline-variant bg-surface-container text-on-surface-disabled';
    else if (lit) surface = 'border-primary bg-primary/5 text-on-surface-variant shadow-glow-soft';
    else if (has && !message) surface = 'border-transparent text-on-surface-variant shadow-glow';
    else surface = 'border-outline text-on-surface-variant';

    const choose = multiple ? 'choose files' : 'choose a file';
    const total = files.reduce((n, f) => n + f.size, 0);

    const prompt = working ? (
      // Reduced motion keeps the cue: the shimmer is motion-safe only, the spinner pulses instead.
      <span className={`flex min-w-0 ${sm ? 'items-center gap-2.5' : 'flex-col items-center gap-1.5'}`}>
        <LogoSpinner size="inline" />
        {has && (
          <span className="flex min-w-0 flex-col" aria-hidden="true">
            <span className={`font-medium text-on-surface [overflow-wrap:anywhere] ${sm ? 'text-[13px] leading-[18px]' : 'text-sm'}`}>
              {files.length === 1 ? files[0].name : `${files.length} files`}
            </span>
            <span className="text-xs text-on-surface-variant tabular-nums">{fileSize(total)}</span>
          </span>
        )}
      </span>
    ) : has ? (
      <FadeIn key={landed} className={`flex min-w-0 ${sm ? 'items-center gap-2.5' : 'flex-col items-center gap-1.5'}`}>
        <span className="flex-none text-primary" aria-hidden="true"><IconCheck draw className={sm ? 'h-4 w-4' : 'h-5 w-5'} /></span>
        <span id={ids.chosen} className="flex min-w-0 flex-col" aria-hidden="true">
          <span className={`font-medium text-on-surface [overflow-wrap:anywhere] ${sm ? 'text-[13px] leading-[18px]' : 'text-sm'}`}>
            {files.length === 1 ? files[0].name : `${files.length} files`}
          </span>
          <span className="text-xs text-on-surface-variant tabular-nums">
            {fileSize(total)}
            {!sm && files.length === 1 && live && (
              <>
                <span className="[@media(pointer:coarse)]:hidden">. Drop another here to replace it.</span>
                <span className="hidden [@media(pointer:coarse)]:inline">. Choose another to replace it.</span>
              </>
            )}
          </span>
        </span>
      </FadeIn>
    ) : (
      <span className={`flex min-w-0 ${sm ? 'items-center gap-2.5' : 'flex-col items-center gap-2'}`} aria-hidden="true">
        <IconUpload lift={lit} className={`flex-none ${sm ? 'h-5 w-5' : 'h-7 w-7'} transition-colors duration-quick ease-emphasized ${lit ? 'text-primary' : disabled ? '' : 'text-on-surface-variant'}`} />
        <span className={sm ? 'text-[13px] leading-[18px]' : 'text-sm'}>
          <span className="[@media(pointer:coarse)]:hidden">
            Drop {what} here or <span className={disabled ? '' : 'text-primary'}>{choose}</span>
          </span>
          <span className="hidden [@media(pointer:coarse)]:inline">
            <span className={disabled ? '' : 'text-primary'}>Choose</span> {what}
          </span>
        </span>
      </span>
    );

    const removeKey = has && onClear && !working ? (
      <Tooltip label="Remove this file">
        <IconButton size="sm" label={files.length === 1 ? `Remove ${files[0].name}` : `Remove the ${files.length} files`} onClick={onClear} disabled={disabled}>
          <IconClose className="h-3.5 w-3.5" />
        </IconButton>
      </Tooltip>
    ) : null;

    return (
      <div
        {...bind}
        data-state={disabled ? 'disabled' : working ? 'busy' : lit ? 'inbound' : has ? 'chosen' : 'empty'}
        className={`relative isolate flex min-w-0 rounded-control border-2 border-dashed transition-[border-color,background-color,box-shadow,--np-layer] duration-standard ease-emphasized has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-primary/70 ${
          message ? 'ring-2 ring-red-500/60' : ''
        } ${live ? 'np-state' : ''} ${working ? 'cursor-progress' : ''} ${
          sm ? 'items-center gap-3 px-3 py-2.5 text-left' : 'min-h-[9.5rem] flex-col items-center justify-center px-6 py-8 text-center'
        } ${surface} ${className}`}
      >
        <input
          title=""
          aria-label={`Choose ${what}`}
          {...inputProps}
          ref={ref}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          aria-disabled={working || undefined}
          aria-describedby={describedBy}
          aria-invalid={message ? true : undefined}
          // Busy keeps the input focusable so a keyboard user's place survives the work.
          onClick={(e) => { if (working) e.preventDefault(); }}
          onChange={(e) => {
            // Copied before the reset: clearing the value lets the same file be chosen again.
            const list = Array.from(e.currentTarget.files || []);
            e.currentTarget.value = '';
            takeIdle(list);
          }}
          // pointer-events-none while it cannot take a file: a disabled input swallows drag
          // events in some engines, and the drop would never reach the zone's handler.
          className={`absolute inset-0 z-0 m-0 h-full w-full cursor-pointer opacity-0 file:cursor-pointer ${live ? '' : 'pointer-events-none'}`}
        />

        {working && (
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[inherit]">
            <span className="absolute inset-0 hidden bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.05),transparent)] motion-safe:block motion-safe:animate-shimmer" />
          </span>
        )}

        <div className={`pointer-events-none relative z-[1] flex min-w-0 flex-col gap-1 ${sm ? 'flex-1' : 'items-center'}`}>
          {prompt}
          <span role="status" className={`text-sm text-on-surface ${working ? '' : 'sr-only'}`}>{working ? busy : ''}</span>
          {hint && (
            <span id={ids.hint} aria-hidden="true" className={`text-xs ${disabled ? '' : 'text-on-surface-variant'} ${sm ? '' : 'mt-0.5'}`}>
              {hint}
            </span>
          )}
          {message && (
            <span id={ids.message} role="alert" className="text-xs text-red-300">{message}</span>
          )}
        </div>

        {removeKey && (sm
          ? <span className="relative z-[2] flex-none">{removeKey}</span>
          : <span className="absolute right-1.5 top-1.5 z-[2]">{removeKey}</span>)}
      </div>
    );
  },
);
DropZone.displayName = 'DropZone';

export interface DropVeilProps {
  /** Usually useFileDrop's `dragging`. */
  show: boolean;
  /** What letting go does: "Drop the invoice to read it". */
  label: string;
}

/** Drawn over a region that takes drops while a file is inbound. The region must be `relative`. */
export const DropVeil: React.FC<DropVeilProps> = ({ show, label }) => {
  const { mounted, open } = usePresence(show, MS.exit);
  if (!mounted) return null;
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-[rgb(25_25_25/0.78)] transition-opacity ${
        open ? 'opacity-100 duration-standard ease-emphasized' : 'opacity-0 duration-exit ease-exit'
      }`}
    >
      <div className="flex items-center gap-3 rounded-control border-2 border-dashed border-primary bg-primary/5 px-6 py-4 text-sm font-medium text-primary-ink shadow-glow-soft">
        <IconUpload lift={open} className="h-6 w-6 flex-none" />
        {label}
      </div>
    </div>
  );
};

export default DropZone;
